import React, { useContext, useState, useMemo, useRef } from 'react';

import cx from 'classnames';
import { partialRight, prop } from 'ramda';

import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useRect } from 'hooks/useRect';

import LandingPageContext from '../../contexts/LandingPageContext';
import LandingPageLayoutContext from '../../contexts/LandingPageLayoutContext';
import LandingPageTypeContext from '../../contexts/LandingPageTypeContext';
import { useContainerScrollPosition } from '../../functions';
import AddNewContent from '../AddNewContent';
import { IAddNewContentWrapperProps } from '../AddNewContent/types';
import ContentWrapper from '../ContentWrapper';
import { getBlockStickyTopOffset, getPageHeaderHeight } from './function';
import s from './index.module.css';
import { IBlockWrapperProps, IGetPageHeaderHeightParams, IWithContentWrapper } from './types';

function BlockWrapper({
  children,
  data,
  isSticky,
  isPortal,
  isPortalItinerary,
  isShownAddNewContent,
  isInsideAccordion,
  indexes,
  onAdd,
  onDelete,
  onUpdate,
  pageHeadlineRef,
  isMobileNavHidden,
  withContentWrapper,
  zeroStickyOffset,
}: IBlockWrapperProps) {
  const blockRef = useRef();
  const [isPopupOpened, setIsPopupOpened] = useState(false);
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { readOnly, landingTheme } = useContext(LandingPageContext);
  const { noDelete = false } = useContext(LandingPageTypeContext);

  const pageHeadlineRect = useRect(pageHeadlineRef);
  const noMenu = data?.data?.noMenu || false;
  const pageHeaderElem = document.getElementById('landingHeader');
  const mobileNavElem = document.getElementById('mobileNav');

  const { headerRef } = useContext(LandingPageLayoutContext);

  const getBlockStickyTopStyle = partialRight(getBlockStickyTopOffset, [
    {
      pageHeaderElem,
      mobileNavElem,
      landingTheme,
      isPortal,
      isTabletOrMobile,
      getHeaderHeight: (params: IGetPageHeaderHeightParams) => getPageHeaderHeight({ ...params, headerRef }),
    },
  ]);

  const blockStickyTopStyle = useMemo(() => {
    if (!isSticky) return 'auto';
    // @ts-ignore
    return getBlockStickyTopStyle(pageHeadlineRect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageHeadlineRect, isSticky]);

  if (!children) return null;

  const withContentWrapperOptions: IWithContentWrapper =
    typeof withContentWrapper === 'object' ? withContentWrapper : {};
  const blockClasses = cx(
    s.block,
    isInsideAccordion && s.accordionBlock,
    isSticky && prop('sticky', s),
    isSticky && isPortal && prop('stickyPortal', s),
    isSticky && isPortalItinerary && prop('stickyItineraryPortal', s),
    zeroStickyOffset && prop('zeroStickyOffset', s),
    isPopupOpened && prop('higherZIndex', s),
    isMobileNavHidden && isSticky && prop('stickyNoMobileNav', s),
    'custom-block',
  );

  return (
    <div className={blockClasses} style={{ top: blockStickyTopStyle }} ref={blockRef as any}>
      <div className={cx(s.childrenWrapper, 'custom-block-children')}>
        {withContentWrapper && !noMenu && !noDelete ? (
          <ContentWrapper
            indexes={indexes}
            onDelete={onDelete}
            onUpdate={onUpdate}
            {...withContentWrapperOptions}
            data={data}
          >
            {children}
          </ContentWrapper>
        ) : (
          children
        )}
      </div>
      {!readOnly && isShownAddNewContent && !noMenu && (
        <AddNewContentWrapper
          onOpenPopup={() => setIsPopupOpened(true)}
          onClosePopup={() => setIsPopupOpened(false)}
          onAdd={onAdd}
          isInsideAccordion={isInsideAccordion}
        />
      )}
    </div>
  );
}

function AddNewContentWrapper({ onOpenPopup, onClosePopup, onAdd, isInsideAccordion }: IAddNewContentWrapperProps) {
  const { isScrolling } = useContainerScrollPosition();

  return (
    <AddNewContent
      className={cx(isInsideAccordion ? s.accordionPlusVisible : s.plusVisible, isScrolling && s.plusNotVisible)}
      onOpenPopup={onOpenPopup}
      onClosePopup={onClosePopup}
      onAdd={onAdd}
      isInsideAccordion={isInsideAccordion}
    />
  );
}

export default BlockWrapper;
